/**
 * PatientCacheProvider — Precarga la lista de pacientes del doctor al iniciar
 * sesión para que Aris (motor de comandos) pueda buscar de forma instantánea
 * sin round-trip al servidor.
 *
 * - Solo carga para roles `doctor` y `secretary`.
 * - Refresca automáticamente cada 5 minutos y ante eventos realtime
 *   `patient:new` / `patient:updated` (soft-invalidate: refetch en background).
 * - Expone `search(query)` que devuelve matches ordenados por relevancia:
 *   1. exact match nombre completo, 2. startsWith cualquier palabra,
 *   3. substring, 4. cédula, 5. teléfono.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import api from "./api";
import { useAuth } from "./auth";
import { useRealtimeEvent } from "./realtime";

const PatientCacheContext = createContext(null);

const norm = (s) =>
  (s || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

export function PatientCacheProvider({ children }) {
  const { user } = useAuth();
  const [patients, setPatients] = useState([]);
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const inflightRef = useRef(false);

  const eligible = !!user && (user.role === "doctor" || user.role === "secretary");

  const load = useCallback(async () => {
    if (!eligible) return;
    if (inflightRef.current) return;
    inflightRef.current = true;
    setLoading(true);
    try {
      const { data } = await api.get("/patients");
      const list = Array.isArray(data) ? data : [];
      // Precompute searchable tokens per patient to speed up search().
      const enriched = list.map((p) => ({
        ...p,
        _name_n: norm(p.full_name),
        _doc_n: norm(p.document_id),
        _phone_n: norm(p.phone),
        _tokens: norm(p.full_name).split(/\s+/).filter(Boolean),
      }));
      setPatients(enriched);
      setReady(true);
    } catch {
      // silencioso — el resto del sistema sigue funcionando sin cache
    } finally {
      setLoading(false);
      inflightRef.current = false;
    }
  }, [eligible]);

  // Carga inicial al login + refresco periódico cada 5 minutos.
  useEffect(() => {
    if (!eligible) {
      setPatients([]);
      setReady(false);
      return undefined;
    }
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [eligible, load]);

  // Realtime: recargar cuando cambien pacientes.
  useRealtimeEvent("patient:new", () => load());
  useRealtimeEvent("patient:updated", () => load());
  useRealtimeEvent("patient:deleted", () => load());

  const search = useCallback(
    (query, limit = 8) => {
      const q = norm(query);
      if (!q) return [];
      const digits = q.replace(/\D/g, "");
      const results = [];
      for (const p of patients) {
        let score = 0;
        if (p._name_n === q) score = 100;
        else if (p._tokens.some((t) => t === q)) score = 90;
        else if (p._tokens.some((t) => t.startsWith(q))) score = 75;
        else if (p._name_n.includes(q)) score = 60;
        else if (digits && p._doc_n.includes(digits)) score = 55;
        else if (digits && p._phone_n.includes(digits)) score = 50;
        if (score > 0) results.push({ patient: p, score });
      }
      results.sort((a, b) => b.score - a.score);
      return results.slice(0, limit).map((r) => r.patient);
    },
    [patients]
  );

  const value = useMemo(
    () => ({ patients, ready, loading, reload: load, search }),
    [patients, ready, loading, load, search]
  );

  return (
    <PatientCacheContext.Provider value={value}>
      {children}
    </PatientCacheContext.Provider>
  );
}

export function usePatientCache() {
  const ctx = useContext(PatientCacheContext);
  if (!ctx) {
    // Fallback seguro si se usa fuera del provider (no debería pasar).
    return { patients: [], ready: false, loading: false, reload: () => {}, search: () => [] };
  }
  return ctx;
}
